import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import api from "../../../core/api/config";
import {
  convocarPacientes,
  exportarConvocacaoCsv,
  fetchConvocacaoRastreamento,
  getConvocacaoMeta,
} from "../services/convocacao.service";

const defaultMeta = getConvocacaoMeta();

const useConvocacaoRastreamento = () => {
  const acessoAtivo = useSelector((state) => state.access.acessoAtivo);
  const [filtros, setFiltros] = useState({
    exame: "TODOS",
    ano: null,
    mes: null,
    busca: "",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState([]);
  const [painelRastreamento, setPainelRastreamento] = useState([]);
  const [statusCards, setStatusCards] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState(defaultMeta.months);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [usuarioId, setUsuarioId] = useState(null);

  useEffect(() => {
    api.get("/api/user/show").then(({ data }) => {
      setUsuarioId(data.user?.id || null);
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchConvocacaoRastreamento({
        exame: filtros.exame,
        ano: filtros.ano,
        mes: filtros.mes,
        busca: filtros.busca,
        idOrganograma: acessoAtivo?.idOrganograma,
        page,
        size: pageSize,
      });

      setDashboard(data.dashboard);
      setPainelRastreamento(data.rastreamentoPainel || []);
      setStatusCards(data.statusCards);
      setYears(data.years);
      setMonths(data.months);
      setRows(data.table.items);
      setTotal(data.table.total);
      setSelectedIds([]);

      if (!filtros.ano && data.years.length > 0) {
        setFiltros((prev) => ({ ...prev, ano: data.years[0].year }));
      }
    } finally {
      setLoading(false);
    }
  }, [acessoAtivo?.idOrganograma, filtros.ano, filtros.busca, filtros.exame, filtros.mes, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const allPageSelected = useMemo(
    () => {
      const selectableRows = rows.filter((row) => row.convocacaoPermitida !== false);
      return selectableRows.length > 0 && selectableRows.every((row) => selectedIds.includes(row.id));
    },
    [rows, selectedIds],
  );

  const toggleSelectRow = (id) => {
    if (rows.find((row) => row.id === id)?.convocacaoPermitida === false) return;

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleSelectPage = () => {
    if (allPageSelected) {
      const pageIds = rows
        .filter((row) => row.convocacaoPermitida !== false)
        .map((row) => row.id);
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }

    const pageIds = rows
      .filter((row) => row.convocacaoPermitida !== false)
      .map((row) => row.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const updateFilter = (next) => {
    setPage(0);
    setFiltros((prev) => ({ ...prev, ...next }));
  };

  const changePage = (nextPage) => {
    if (nextPage < 0) return;
    if (nextPage > Math.ceil(total / pageSize) - 1) return;
    setPage(nextPage);
  };

  const changePageSize = (size) => {
    setPage(0);
    setPageSize(size);
  };

  const confirmarConvocacao = async () => {
    const resultado = await Swal.fire({
      title: "Confirmar convocação?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#67748e",
    });

    return resultado.isConfirmed;
  };

  const convocarSelecionadas = async () => {
    if (selectedIds.length === 0 || usuarioId == null) return;
    if (!(await confirmarConvocacao(selectedIds.length))) return;
    await convocarPacientes({ tipo: "rastreamento", ids: selectedIds, usuarioId });
    await loadData();
  };

  const convocarPaciente = async (id) => {
    if (usuarioId == null) return;
    if (!(await confirmarConvocacao(1))) return;
    await convocarPacientes({ tipo: "rastreamento", ids: [id], usuarioId });
    await loadData();
  };

  const exportarCsv = async () => {
    const arquivo = await exportarConvocacaoCsv({
      tipo: "rastreamento",
      filtros: { ...filtros, idOrganograma: acessoAtivo?.idOrganograma },
    });
    const blob = new Blob([arquivo], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "convocacao-rastreamento.xls";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return {
    meta: defaultMeta,
    filtros,
    loading,
    dashboard,
    painelRastreamento,
    statusCards,
    years,
    months,
    rows,
    total,
    page,
    pageSize,
    selectedIds,
    clearSelection,
    allPageSelected,
    updateFilter,
    toggleSelectRow,
    toggleSelectPage,
    changePage,
    changePageSize,
    convocarPaciente,
    convocarSelecionadas,
    exportarCsv,
  };
};

export default useConvocacaoRastreamento;
